import React from "react";
import {
  Box,
  CircularProgress,
  FormControl,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import config from "../config";
import ImageCard from "../components/ImageCard";
import GalleryMasonry from "../components/GalleryMasonry";
import { useAuth } from "../auth/AuthContext";
import ImageDetailDrawer from "../components/ImageDetailDrawer";

const sortOptions = [
  { value: "created_at", label: "Uploaded" },
  { value: "filename", label: "Filename" },
  { value: "size", label: "File Size" },
  { value: "width", label: "Width" },
  { value: "height", label: "Height" },
  { value: "aspect_ratio", label: "Aspect Ratio" },
];

const ImagesPage = () => {
  const [images, setImages] = React.useState([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState(null);
  const [detailOpen, setDetailOpen] = React.useState(false);
  const [selected, setSelected] = React.useState(null);
  const [searchInput, setSearchInput] = React.useState("");
  const [debouncedSearch, setDebouncedSearch] = React.useState("");
  const [sortField, setSortField] = React.useState("created_at");
  const [refreshKey, setRefreshKey] = React.useState(0);
  const [sortDirection, setSortDirection] = React.useState("desc");
  const { fetchWithAuth } = useAuth();

  React.useEffect(() => {
    const handle = window.setTimeout(() => {
      setDebouncedSearch(searchInput.trim().toLowerCase());
    }, 300);
    return () => window.clearTimeout(handle);
  }, [searchInput]);

  React.useEffect(() => {
    let isMounted = true;
    const fetchImages = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams();
        if (debouncedSearch) {
          params.set("q", debouncedSearch);
        }
        if (sortField) {
          params.set("sort", sortField);
        }
        if (sortDirection) {
          params.set("direction", sortDirection);
        }
        const url =
          params.toString() === ""
            ? `${config.apiBaseUrl}/images`
            : `${config.apiBaseUrl}/images?${params.toString()}`;
        const response = await fetchWithAuth(url);
        if (!response.ok) {
          throw new Error("Failed to load images");
        }
        const payload = await response.json();
        if (isMounted) {
          setImages(payload.items || []);
        }
      } catch (err) {
        if (isMounted) {
          setError(err.message);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchImages();

    return () => {
      isMounted = false;
    };
  }, [fetchWithAuth, debouncedSearch, sortField, sortDirection, refreshKey]);

  React.useEffect(() => {
    if (typeof window === "undefined") {
      return undefined;
    }

    const handleNewImage = (event) => {
      const newImage = event.detail;
      if (!newImage) {
        return;
      }
      setRefreshKey((prev) => prev + 1);
    };

    window.addEventListener("gallery:imageUploaded", handleNewImage);
    return () => {
      window.removeEventListener("gallery:imageUploaded", handleNewImage);
    };
  }, []);

  const handleItemClick = React.useCallback((image) => {
    setSelected(image);
    setDetailOpen(true);
  }, []);

  const handleCloseDetail = React.useCallback(() => {
    setDetailOpen(false);
    setSelected(null);
  }, []);

  const handleDeleteImage = React.useCallback(
    async (image) => {
      if (!image) {
        return;
      }
      try {
        const response = await fetchWithAuth(
          `${config.apiBaseUrl}/images/${image.id}`,
          { method: "DELETE" },
        );
        if (!response.ok && response.status !== 204) {
          throw new Error("Failed to delete image");
        }
        setImages((prev) => prev.filter((item) => item.id !== image.id));
        setSelected(null);
        setDetailOpen(false);
      } catch (err) {
        setError(err.message || "Failed to delete image");
      }
    },
    [fetchWithAuth],
  );

  return (
    <>
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          height: "100%",
          display: "flex",
          flexDirection: "column",
          gap: 2,
        }}
      >
        <Box
          sx={{
            position: "sticky",
            top: 0,
            zIndex: 3,
            backgroundColor: "background.default",
            pb: 2,
          }}
        >
          <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
            <TextField
              fullWidth
              value={searchInput}
              onChange={(event) => setSearchInput(event.target.value)}
              placeholder="Search by filename"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
              size="small"
            />
            <FormControl
              size="small"
              sx={{ minWidth: { xs: "100%", md: 200 } }}
            >
              <InputLabel id="sort-label">Sort By</InputLabel>
              <Select
                labelId="sort-label"
                label="Sort By"
                value={sortField}
                onChange={(event) => setSortField(event.target.value)}
              >
                {sortOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl
              size="small"
              sx={{ minWidth: { xs: "100%", md: 120 } }}
            >
              <InputLabel id="direction-label">Order</InputLabel>
              <Select
                labelId="direction-label"
                label="Order"
                value={sortDirection}
                onChange={(event) => setSortDirection(event.target.value)}
              >
                <MenuItem value="asc">▲ ASC</MenuItem>
                <MenuItem value="desc">▼ DESC</MenuItem>
              </Select>
            </FormControl>
          </Stack>
        </Box>

        {loading ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="error">{error}</Typography>
          </Box>
        ) : !images.length ? (
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <Typography color="text.secondary">
              No images uploaded yet.
            </Typography>
          </Box>
        ) : (
          <GalleryMasonry
            images={images}
            renderItem={(item, columnWidth) => (
              <ImageCard
                image={item}
                width={columnWidth}
                onClick={() => handleItemClick(item)}
              />
            )}
          />
        )}
      </Box>
      <ImageDetailDrawer
        open={detailOpen}
        onClose={handleCloseDetail}
        image={selected}
        onDelete={handleDeleteImage}
      />
    </>
  );
};

export default ImagesPage;
